import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { calcMonthly } from "@/lib/providers/index";
import SandboxBadge from "@/components/SandboxBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plane, MapPin } from "lucide-react";

// Import assets
import heroTravelImg from "@/assets/hero-travel.jpg";
import dubaiMarinaImg from "@/assets/stay-dubai-marina.jpg";
import santoriniImg from "@/assets/stay-santorini.jpg";
import tokyoImg from "@/assets/stay-tokyo.jpg";
import londonImg from "@/assets/stay-london.jpg";
import parisImg from "@/assets/stay-paris.jpg";
import bangkokImg from "@/assets/stay-bangkok.jpg";
import istanbulImg from "@/assets/stay-istanbul.jpg";
import singaporeImg from "@/assets/stay-singapore.jpg";
import romeImg from "@/assets/stay-rome.jpg";
import maldivesImg from "@/assets/stay-maldives.jpg";
import newyorkImg from "@/assets/stay-newyork.jpg";
import niceImg from "@/assets/stay-nice.jpg";
import emiratesLogo from "@/assets/airlines/emirates-logo.png";
import airfranceLogo from "@/assets/airlines/airfrance-logo.png";
import jalLogo from "@/assets/airlines/jal-logo.png";

export default function Travel() {
  const [searchType, setSearchType] = useState<"flights" | "stays" | "packages">("packages");

  // Example data to showcase flex payments
  const exampleFlights = [
    { route: "Dubai → London", price: 2400, carrier: "Emirates", duration: "7h 30m", logo: emiratesLogo },
    { route: "Dubai → New York", price: 3200, carrier: "Emirates", duration: "12h 45m", logo: emiratesLogo },
    { route: "Dubai → Paris", price: 2800, carrier: "Air France", duration: "8h 15m", logo: airfranceLogo },
    { route: "Dubai → Tokyo", price: 2200, carrier: "JAL", duration: "9h 15m", logo: jalLogo },
    { route: "Dubai → Bangkok", price: 1800, carrier: "Emirates", duration: "6h 45m", logo: emiratesLogo },
    { route: "Dubai → Singapore", price: 2000, carrier: "Emirates", duration: "7h 20m", logo: emiratesLogo },
    { route: "Dubai → Rome", price: 2600, carrier: "Emirates", duration: "7h 50m", logo: emiratesLogo },
    { route: "Dubai → Istanbul", price: 1600, carrier: "Emirates", duration: "4h 30m", logo: emiratesLogo },
    { route: "Dubai → Mumbai", price: 1400, carrier: "Air France", duration: "3h 15m", logo: airfranceLogo },
  ];

  const exampleStays = [
    { name: "Marina View Hotel", city: "Dubai", price: 1800, nights: 3, image: dubaiMarinaImg },
    { name: "Santorini Luxury Suite", city: "Greece", price: 3200, nights: 4, image: santoriniImg },
    { name: "Tokyo Business Hotel", city: "Japan", price: 2200, nights: 3, image: tokyoImg },
    { name: "London Central Hotel", city: "United Kingdom", price: 2800, nights: 4, image: londonImg },
    { name: "Champs-Élysées Suite", city: "France", price: 3600, nights: 5, image: parisImg },
    { name: "Riverside Bangkok Hotel", city: "Thailand", price: 1600, nights: 3, image: bangkokImg },
    { name: "Bosphorus View Hotel", city: "Turkey", price: 1400, nights: 3, image: istanbulImg },
    { name: "Marina Bay Suites", city: "Singapore", price: 2600, nights: 4, image: singaporeImg },
    { name: "Colosseum View Hotel", city: "Italy", price: 2400, nights: 4, image: romeImg },
  ];

  const travelPackages = [
    {
      id: "1",
      destination: "Dubai Experience",
      flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Marina View Hotel", nights: 4, image: dubaiMarinaImg },
      totalPrice: 4200,
      savings: 400
    },
    {
      id: "2", 
      destination: "Greek Island Getaway",
      flight: { route: "Dubai → Athens → Dubai", carrier: "Air France", logo: airfranceLogo },
      stay: { name: "Santorini Luxury Suite", nights: 5, image: santoriniImg },
      totalPrice: 6000,
      savings: 600
    },
    {
      id: "3",
      destination: "Tokyo Adventure", 
      flight: { route: "Dubai → Tokyo → Dubai", carrier: "JAL", logo: jalLogo },
      stay: { name: "Tokyo Business Hotel", nights: 4, image: tokyoImg },
      totalPrice: 4400,
      savings: 350
    },
    {
      id: "4",
      destination: "London City Break",
      flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "London Central Hotel", nights: 3, image: londonImg },
      totalPrice: 3800,
      savings: 300
    },
    {
      id: "5",
      destination: "Paris Romance",
      flight: { route: "Dubai → Paris → Dubai", carrier: "Air France", logo: airfranceLogo },
      stay: { name: "Champs-Élysées Suite", nights: 4, image: parisImg },
      totalPrice: 5200,
      savings: 520
    },
    {
      id: "6",
      destination: "Mediterranean Escape",
      flight: { route: "Dubai → Nice → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Riviera Resort", nights: 5, image: niceImg },
      totalPrice: 5800,
      savings: 450
    },
    {
      id: "7",
      destination: "New York Explorer",
      flight: { route: "Dubai → New York → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Manhattan Plaza Hotel", nights: 6, image: newyorkImg },
      totalPrice: 7200,
      savings: 800
    },
    {
      id: "8",
      destination: "Bangkok Discovery",
      flight: { route: "Dubai → Bangkok → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Riverside Bangkok Hotel", nights: 4, image: bangkokImg },
      totalPrice: 3600,
      savings: 280
    },
    {
      id: "9",
      destination: "Istanbul Cultural Tour",
      flight: { route: "Dubai → Istanbul → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Bosphorus View Hotel", nights: 4, image: istanbulImg },
      totalPrice: 3400,
      savings: 250
    },
    {
      id: "10",
      destination: "Singapore Luxury",
      flight: { route: "Dubai → Singapore → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Marina Bay Suites", nights: 5, image: singaporeImg },
      totalPrice: 4800,
      savings: 380
    },
    {
      id: "11",
      destination: "Rome Historic Journey",
      flight: { route: "Dubai → Rome → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Colosseum View Hotel", nights: 4, image: romeImg },
      totalPrice: 4600,
      savings: 320
    },
    {
      id: "12",
      destination: "Maldives Paradise",
      flight: { route: "Dubai → Malé → Dubai", carrier: "Emirates", logo: emiratesLogo },
      stay: { name: "Overwater Villa Resort", nights: 7, image: maldivesImg },
      totalPrice: 8500,
      savings: 900
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-20">
        {/* Hero Section */}
        <section 
          className="relative py-24 px-4 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroTravelImg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary-glow/70 to-accent/60"></div>
          <div className="relative max-w-6xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Flex Travel
            </h1>
            <SandboxBadge />
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-95">
              Search flights and stays, then spread the cost with trusted BNPL providers. Travel now, pay later.
            </p>

            {/* Search Type Toggle */}
            <div className="inline-flex rounded-xl overflow-hidden border border-border mb-8 bg-card">
              <button 
                onClick={() => setSearchType("flights")}
                className={`px-6 py-3 font-medium transition-all ${
                  searchType === 'flights' 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-foreground hover:bg-accent'
                }`}
              >
                Search Flights
              </button>
              <button 
                onClick={() => setSearchType("stays")}
                className={`px-6 py-3 font-medium transition-all ${
                  searchType === 'stays' 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-foreground hover:bg-accent'
                }`}
              >
                Search Stays
              </button>
              <button 
                onClick={() => setSearchType("packages")}
                className={`px-6 py-3 font-medium transition-all ${
                  searchType === 'packages' 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-foreground hover:bg-accent'
                }`}
              >
                Search Travel Packages
              </button>
            </div>
          </div>
        </section>

        {/* Search Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {searchType === "flights" ? (
              <FlightSearch />
            ) : searchType === "stays" ? (
              <StaySearch />
            ) : (
              <PackageSearch />
            )}
          </div>
        </section>

        {/* Examples Section */}
        <section className="py-16 px-4 bg-accent/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              {searchType === "flights" ? "Popular Flight Routes" : 
               searchType === "stays" ? "Featured Stays" : 
               "Featured Travel Packages"}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {searchType === "flights" ? 
                exampleFlights.slice(0, 9).map((item, index) => (
                  <Card key={index} className="p-6 hover:shadow-lg transition-shadow bg-card border-border">
                    <FlightCard flight={item} />
                  </Card>
                )) :
                searchType === "stays" ?
                exampleStays.slice(0, 9).map((item, index) => (
                  <Card key={index} className="p-6 hover:shadow-lg transition-shadow bg-card border-border">
                    <StayCard stay={item} />
                  </Card>
                )) :
                travelPackages.map((pkg) => (
                  <Card key={pkg.id} className="p-6 hover:shadow-lg transition-shadow bg-card border-border">
                    <PackageCard package={pkg} />
                  </Card>
                ))
              }
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function FlightSearch() {
  return (
    <Card className="p-8 bg-card border-border">
      <h3 className="text-2xl font-semibold mb-6 text-foreground">Search Flights</h3>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">From</label>
          <input 
            type="text" 
            placeholder="DXB" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">To</label>
          <input 
            type="text" 
            placeholder="LHR" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Departure</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Return</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
      <Button className="w-full md:w-auto px-8 py-3 text-lg">
        Search Flights
      </Button>
    </Card>
  );
}

function StaySearch() {
  return (
    <Card className="p-8 bg-card border-border">
      <h3 className="text-2xl font-semibold mb-6 text-foreground">Search Stays</h3>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Destination</label>
          <input 
            type="text" 
            placeholder="Dubai, UAE" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Check-in</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Check-out</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
      <Button className="w-full md:w-auto px-8 py-3 text-lg">
        Search Stays
      </Button>
    </Card>
  );
}

function PackageSearch() {
  return (
    <Card className="p-8 bg-card border-border">
      <h3 className="text-2xl font-semibold mb-6 text-foreground">Search Travel Packages</h3>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Destination</label>
          <input 
            type="text" 
            placeholder="Tokyo, Japan" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Check-in</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Check-out</label>
          <input 
            type="date" 
            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">Travelers</label>
          <select className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent">
            <option>1 Adult</option>
            <option>2 Adults</option>
            <option>3 Adults</option>
            <option>4 Adults</option>
          </select>
        </div>
      </div>
      <Button className="w-full md:w-auto px-8 py-3 text-lg">
        Search Packages
      </Button>
    </Card>
  );
}

function FlightCard({ flight }: { flight: { route: string; price: number; carrier: string; duration: string; logo: string } }) {
  const monthlyPayment = calcMonthly(flight.price, 12);
  
  return (
    <>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h4 className="font-semibold text-lg text-foreground">{flight.route}</h4>
          <div className="flex items-center gap-2 mt-1">
            <img src={flight.logo} alt={flight.carrier} className="w-8 h-8 object-contain" />
            <p className="text-muted-foreground">{flight.carrier}</p>
          </div>
        </div>
        <span className="text-sm text-muted-foreground">{flight.duration}</span>
      </div>
      
      <div className="mb-4">
        <div className="text-2xl font-bold text-foreground">AED {flight.price.toLocaleString()}</div>
      </div>
      
      <div className="bg-accent/10 rounded-lg p-4 mb-4">
        <div className="text-sm text-muted-foreground">Flex Payment Example:</div>
        <div className="text-lg font-semibold text-primary">AED {monthlyPayment}/month</div>
        <div className="text-xs text-muted-foreground">Over 12 months • Subject to approval</div>
      </div>
      
      <Button variant="outline" className="w-full">
        Select Flight
      </Button>
    </>
  );
}

function StayCard({ stay }: { stay: { name: string; city: string; price: number; nights: number; image: string } }) {
  const monthlyPayment = calcMonthly(stay.price, 12);
  
  return (
    <>
      <div className="relative w-full h-40 mb-4 rounded-lg overflow-hidden">
        <img 
          src={stay.image} 
          alt={stay.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
          {stay.nights} nights
        </div>
      </div>
      
      <div className="mb-4">
        <h4 className="font-semibold text-lg text-foreground">{stay.name}</h4>
        <div className="flex items-center gap-1 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <p>{stay.city}</p>
        </div>
      </div>
      
      <div className="mb-4">
        <div className="text-2xl font-bold text-foreground">AED {stay.price.toLocaleString()}</div>
      </div>
      
      <div className="bg-accent/10 rounded-lg p-4 mb-4">
        <div className="text-sm text-muted-foreground">Flex Payment Example:</div>
        <div className="text-lg font-semibold text-primary">AED {monthlyPayment}/month</div>
        <div className="text-xs text-muted-foreground">Over 12 months • Subject to approval</div>
      </div>
      
      <Button variant="outline" className="w-full">
        Book Stay
      </Button>
    </>
  );
}

function PackageCard({ package: pkg }: { package: any }) {
  const monthlyPayment = calcMonthly(pkg.totalPrice, 12);
  
  return (
    <>
      <div className="relative w-full h-40 mb-4 rounded-lg overflow-hidden">
        <img 
          src={pkg.stay.image} 
          alt={pkg.destination}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-semibold">
          Save AED {pkg.savings}
        </div>
      </div>
      
      <div className="mb-4">
        <h4 className="font-semibold text-lg text-foreground">{pkg.destination}</h4>
      </div>

      {/* Flight Details */}
      <div className="flex items-center gap-2 mb-2 p-2 bg-accent/10 rounded">
        <Plane className="w-4 h-4 text-primary" />
        <img src={pkg.flight.logo} alt={pkg.flight.carrier} className="w-6 h-6 object-contain" />
        <span className="text-sm text-foreground">{pkg.flight.route}</span>
      </div>

      {/* Stay Details */}
      <div className="flex items-center gap-2 mb-4 p-2 bg-accent/10 rounded">
        <MapPin className="w-4 h-4 text-primary" />
        <span className="text-sm text-foreground">{pkg.stay.name} • {pkg.stay.nights} nights</span>
      </div>
      
      <div className="mb-4">
        <div className="text-2xl font-bold text-foreground">AED {pkg.totalPrice.toLocaleString()}</div>
        <div className="text-sm text-muted-foreground line-through">AED {(pkg.totalPrice + pkg.savings).toLocaleString()}</div>
      </div>
      
      <div className="bg-accent/10 rounded-lg p-4 mb-4">
        <div className="text-sm text-muted-foreground">Flex Payment Example:</div>
        <div className="text-lg font-semibold text-primary">AED {monthlyPayment}/month</div>
        <div className="text-xs text-muted-foreground">Over 12 months • Subject to approval</div>
      </div>
      
      <Link to={`/travel/package-booking/${pkg.id}`}>
        <Button variant="outline" className="w-full">
          Book Package
        </Button>
      </Link>
    </>
  );
}